import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HiEye } from "react-icons/hi";

import * as types from "types";
import styles from "./Message.module.scss";
import DeleteFileButton from "./DeleteFileButton";
import DownloadFileButton from "./DownloadFileButton";
import ShareFileButton from "./ShareFileButton";
import ImagePreview from "./ImagePreview";
import FilePreviewModal, {
  findVariantPermalink,
  getFilePreviewTarget,
} from "./FilePreviewModal";

export interface MessageFileProps {
  message: types.FileMessage;
  chatThread: types.ChatThread;
  setMessages?: React.Dispatch<React.SetStateAction<types.Message[]>>;
}

/**
 * Represents a chat file message.
 */
export default function MessageFile({ message, chatThread }: MessageFileProps) {
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);

  const fileMeta = message.meta;
  const fileName = message.content;
  const isImage = fileName.match(/\.(jpeg|jpg|png)$/) !== null;

  const variants = fileMeta?.variants || [];
  const originalPermalink = findVariantPermalink(variants, "original");
  const fileId = fileMeta?.file_id || null;
  const isLegacy = types.isLegacyFileMessage(message);
  // Legacy files have no bytes left to preview.
  const previewTarget = isLegacy
    ? null
    : getFilePreviewTarget(fileName, variants);
  return (
    // maxWidth here is meant to make messages more readable on larger screens
    // Tachyon's `measure-wide` was too narrow.
    <>
      <div className={styles.messageContainer}>
        <span className={styles.messageRole}>File</span>
        <div className={styles.message}>
          {/* Display picture */}
          <div className={`${styles.displayPic} user`}>
            <FontAwesomeIcon
              icon={"file"}
              className={`${styles.filePic} fa-lg`}
            />
          </div>
          {/* Role and message content */}
          <div className={`${styles.messageContent} user`}>
            <div className="flex items-center justify-between">
              <span>
                File Name: {fileName}
                {isLegacy && (
                  <span className={styles.legacyFileWarning}>
                    No longer available — please re-upload
                  </span>
                )}
              </span>
              <div className="flex items-center" style={{ gap: "1.1rem" }}>
                {previewTarget && (
                  <button
                    className={`${styles.deleteFile} bg-transparent bn pa0 dim pointer gray`}
                    onClick={() => setIsPreviewOpen(true)}
                    title="Preview file"
                  >
                    <HiEye size={18} />
                  </button>
                )}

                {!isLegacy && fileId && (
                  <ShareFileButton fileName={fileName} fileId={fileId} />
                )}

                {!isLegacy && (
                  <DownloadFileButton
                    fileName={fileName}
                    chatThread={chatThread}
                    permalink={originalPermalink}
                  />
                )}
                <DeleteFileButton
                  chatThread={chatThread}
                  fileId={fileId}
                  messageUuid={message.uuid}
                />
              </div>
            </div>
            {isImage && !isLegacy && (
              <div className={`mt3 ${styles.fullWidth}`}>
                <ImagePreview
                  fileName={fileName}
                  chatThread={chatThread}
                  permalink={originalPermalink}
                />
              </div>
            )}
            {isPreviewOpen && previewTarget && (
              <FilePreviewModal
                fileName={fileName}
                chatThread={chatThread}
                permalink={previewTarget.permalink}
                previewKindOverride={previewTarget.previewKindOverride}
                downloadPermalink={previewTarget.downloadPermalink}
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
              />
            )}
          </div>
          <div className={styles.avatarSpacer} aria-hidden="true" />
        </div>
      </div>
    </>
  );
}
